import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/badge';
import { Icon } from '@/shared/icons';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useAlerts } from '../hooks';
import { CreditAlertForm } from './CreditAlertForm';
import type { CreditAlert, CreateCreditAlertRequest } from '../types';

const TRIGGER_LABELS: Record<string, string> = {
  balance_below: 'Saldo abaixo de',
  daily_consumption_above: 'Consumo diario acima de',
};

const CHANNEL_LABELS: Record<string, string> = {
  socket: 'Notificacao',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
};

const formatNumber = (n: number) =>
  new Intl.NumberFormat('pt-BR').format(n);

/**
 * List of credit alerts with management actions.
 */
export const AlertsPage: React.FC = () => {
  const { alerts, isLoading, createAlert, isCreating, updateAlert, isUpdating, deleteAlert, isDeleting } = useAlerts();
  const [showForm, setShowForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState<{ alert: CreditAlert; index: number } | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const handleCreate = (data: CreateCreditAlertRequest) => {
    createAlert(data, {
      onSuccess: () => setShowForm(false),
    });
  };

  const handleUpdate = (data: CreateCreditAlertRequest) => {
    if (editingAlert === null) return;
    updateAlert(
      { index: editingAlert.index, data },
      { onSuccess: () => setEditingAlert(null) },
    );
  };

  const confirmDelete = (index: number) => {
    deleteAlert(index, {
      onSuccess: () => setDeletingIndex(null),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton width="200px" height={24} />
          <Skeleton width="160px" height={36} variant="rectangular" />
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} variant="card" height="100px" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Card único: card-h (título + ação) + alertas como linhas compactas (mockup) */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Alertas de crédito
              </h3>
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Configure alertas para monitorar seu saldo
              </p>
            </div>
            <Button size="sm" onClick={() => setShowForm(true)}>
              <Icon name="plus" className="h-4 w-4 mr-2" />
              Adicionar Alerta
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {!alerts || alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: 'var(--theme-card-border)' }}
              >
                <Icon name="bell" className="h-6 w-6" style={{ color: 'var(--theme-muted-text)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Nenhum alerta configurado
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Crie alertas para ser notificado sobre seus créditos
              </p>
            </div>
          ) : (
            <div>
              {alerts.map((alert: CreditAlert, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t"
                  style={{ borderColor: 'var(--theme-card-border)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: alert.enabled ? '#ede9fe' : 'var(--theme-card-border)' }}
                    >
                      <Icon
                        name="bell"
                        className="h-4 w-4"
                        style={{ color: alert.enabled ? '#8b5cf6' : 'var(--theme-muted-text)' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                        {TRIGGER_LABELS[alert.trigger.type] || alert.trigger.type}{' '}
                        <span className="font-semibold">{formatNumber(alert.trigger.value)}</span>
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {alert.notification.channels.map((ch) => (
                          <Badge key={ch} variant="secondary">
                            {CHANNEL_LABELS[ch] || ch}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={alert.enabled ? 'success' : 'secondary'} dot>
                      {alert.enabled ? 'Ativo' : 'Inativo'}
                    </Badge>
                    <Button variant="secondary" size="sm" onClick={() => setEditingAlert({ alert, index })}>
                      <Icon name="edit" className="h-3.5 w-3.5 mr-1" />
                      Editar
                    </Button>
                    {deletingIndex === index ? (
                      <>
                        <Button variant="error" size="sm" onClick={() => confirmDelete(index)} loading={isDeleting}>
                          Confirmar
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => setDeletingIndex(null)}>
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setDeletingIndex(index)}>
                        <Icon name="trash" className="h-3.5 w-3.5 mr-1" />
                        Remover
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create form */}
      <CreditAlertForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
      />

      {/* Edit form */}
      {editingAlert && (
        <CreditAlertForm
          isOpen
          onClose={() => setEditingAlert(null)}
          alert={editingAlert.alert}
          alertIndex={editingAlert.index}
          onSubmit={handleUpdate}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

AlertsPage.displayName = 'AlertsPage';
